import { END_NODE_STEP } from "@redotech/redo-return-ui/flows/return-flow/end-node";
import {
  PHASE_ANCHOR_STEP,
  PHASE_ORDER,
} from "@redotech/redo-return-ui/flows/return-flow/phase-anchor";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Edge, MarkerType } from "@xyflow/react";
import { safeToModel } from "../validation/safe-to-model";
import { PhaseLayoutSummary } from "./nodes-from-steps";

function findEndNodeId(steps: Map<StepId, Step>): StepId | undefined {
  for (const [stepId, step] of steps) {
    if (safeToModel(step)?.type === END_NODE_STEP) {
      return stepId;
    }
  }
  return undefined;
}

function phaseFallthroughTarget(
  phase: string,
  phaseLayout: PhaseLayoutSummary,
  endNodeId: StepId | undefined,
): StepId | undefined {
  const phaseIndex = PHASE_ORDER.indexOf(phase as (typeof PHASE_ORDER)[number]);
  if (phaseIndex < 0) {
    return undefined;
  }
  if (phaseIndex >= PHASE_ORDER.length - 1) {
    return endNodeId;
  }
  const nextPhase = PHASE_ORDER[phaseIndex + 1];
  return nextPhase ? phaseLayout.anchorByPhase.get(nextPhase) : undefined;
}

function phantomEdge(
  sourceId: StepId,
  sourceHandle: string,
  targetId: StepId,
  index: number,
  totalEdges: number,
  label?: string,
  // An empty phase anchor renders its fall-through as a normal solid line, so
  // an empty phase reads as connected to the next phase. Every other phantom
  // (a step's unfilled branch) keeps the lighter dashed "suggestion" style.
  solid = false,
): Edge {
  return {
    id: `phantom-${sourceId}-${targetId}-${index}`,
    source: sourceId,
    sourceHandle,
    target: targetId,
    targetHandle: `${targetId}-target`,
    label,
    type: "customFlow",
    style: solid
      ? { stroke: "black", strokeWidth: 1 }
      : { stroke: "#98a2b3", strokeWidth: 1, strokeDasharray: "4 4" },
    markerEnd: { type: MarkerType.Arrow, color: solid ? "black" : "#98a2b3" },
    data: { edgeIndex: index, totalEdges, phantom: true },
    selectable: false,
    deletable: false,
    focusable: false,
  };
}

/**
 * For Compensation-style flows, generate "phantom" edges that communicate the
 * implicit fall-through to the next phase (or End in the Delivered phase).
 *
 * Two sources of phantoms:
 *
 * 1. Any unfilled downstream slot on a non-anchor step (e.g. a Condition's
 *    empty True/False branch). The slot's placeholder handle gets a dashed
 *    edge to the fall-through target.
 * 2. Any phase anchor with no `next`. An empty phase persists as
 *    `trigger.nextId === trigger.id` and reloads with `anchor.next === null`;
 *    the dashed edge from the anchor tells the user that the runtime moves to
 *    the next phase automatically without requiring them to wire it.
 *
 * Phantom edges are render-only: they don't exist in the model and aren't
 * persisted.
 */
export function buildPhantomPhaseEdges(
  steps: Map<StepId, Step>,
  phaseLayout: PhaseLayoutSummary | undefined,
): Edge[] {
  if (!phaseLayout) {
    return [];
  }

  const endNodeId = findEndNodeId(steps);

  const phantomEdges: Edge[] = [];
  for (const [stepId, step] of steps) {
    const phase = phaseLayout.phaseOfNode.get(stepId);
    if (!phase) {
      continue;
    }
    if (stepId === endNodeId) {
      // The End node has no downstream and never falls through.
      continue;
    }

    const targetId = phaseFallthroughTarget(phase, phaseLayout, endNodeId);
    if (!targetId || targetId === stepId) {
      continue;
    }

    const isAnchor = phaseLayout.anchorByPhase.get(phase) === stepId;
    if (isAnchor) {
      // The anchor is its phase's entry point. Render a phantom only when its
      // single `next` slot is empty, signalling an empty phase / cross-phase
      // fall-through that the codec drops on save.
      const model = safeToModel(step);
      if (model?.type !== PHASE_ANCHOR_STEP || model.next != null) {
        continue;
      }
      phantomEdges.push(
        phantomEdge(
          stepId,
          // FlowStepNode renders one placeholder handle per empty downstream
          // slot, indexed by handle position. An anchor has at most one slot,
          // so the index is always 0.
          `${stepId}-placeholder-0`,
          targetId,
          0,
          1,
          undefined,
          true,
        ),
      );
      continue;
    }

    const downstreams = step.downstream();
    if (downstreams.length === 0) {
      // Step with no downstream slots at all (e.g. terminal). Don't add a
      // phantom — that's a true terminal, the phase ends there.
      continue;
    }
    for (const [index, downstream] of downstreams.entries()) {
      if (downstream.id) {
        continue;
      }
      phantomEdges.push(
        phantomEdge(
          stepId,
          // Match the placeholder handle id that FlowStepNode generates for
          // unfilled downstream slots: `${stepId}-placeholder-${handleIndex}`.
          // Otherwise React Flow can't find the source handle and routes the
          // edge from the node center, making the arrow appear to terminate
          // in the middle of the target card.
          `${stepId}-placeholder-${index}`,
          targetId,
          index,
          downstreams.length,
          downstream.label,
        ),
      );
    }
  }
  return phantomEdges;
}
